import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AuthPresentationSurface } from '../../api/generated/model'
import {
  getAdminAuthPresentation,
  publishAuthPresentation,
  restoreDefaultAuthPresentation,
} from '../../api/auth-presentation'
import { AuthHeroEditor } from '../../components/auth/AuthHeroEditor'
import { useT } from '../../i18n'
import { loginExperienceI18n } from '../../i18n/admin/login-experience'

export function LoginExperienceSettings() {
  const t = useT(loginExperienceI18n)
  const queryClient = useQueryClient()
  const presentation = useQuery({
    queryKey: ['admin-auth-presentation'],
    queryFn: getAdminAuthPresentation,
  })
  const publish = useMutation({
    mutationFn: async (input: {
      surface: AuthPresentationSurface
      file: File
      altTextEn: string
      altTextTr: string
      focalX: number
      focalY: number
    }) => publishAuthPresentation(input.surface, input.file, {
      alt_text_en: input.altTextEn,
      alt_text_tr: input.altTextTr.trim() || null,
      focal_x: input.focalX,
      focal_y: input.focalY,
    }),
    onSuccess: async (_, input) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-auth-presentation'] }),
        queryClient.invalidateQueries({ queryKey: ['public-auth-presentation', input.surface] }),
      ])
    },
  })
  const restore = useMutation({
    mutationFn: restoreDefaultAuthPresentation,
    onSuccess: async (_, surface) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-auth-presentation'] }),
        queryClient.invalidateQueries({ queryKey: ['public-auth-presentation', surface] }),
      ])
    },
  })

  if (presentation.isLoading) {
    return <div className="rounded-[var(--card-radius)] border border-[var(--card-border)] bg-white p-6 text-sm text-[var(--color-text-muted)]">{t.loading}</div>
  }
  if (!presentation.data) {
    return <div role="alert" className="rounded-[var(--card-radius)] border border-red-200 bg-red-50 p-6 text-sm text-red-700">{t.loadError} {presentation.error instanceof Error ? presentation.error.message : ''}</div>
  }

  return (
    <div className="mx-auto max-w-[86rem] space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[var(--color-signal)]">{t.eyebrow}</p>
          <h1 className="mt-1 text-2xl font-[680] tracking-[-0.035em] text-[var(--color-text)]">{t.title}</h1>
          <p className="mt-1 max-w-3xl text-sm leading-6 text-[var(--color-text-muted)]">{t.description}</p>
        </div>
        <div className="rounded-full border border-[var(--color-border)] bg-white px-3 py-1.5 text-[11px] font-semibold text-[var(--color-text-muted)]">{t.publishIndependently}</div>
      </header>

      {(publish.error || restore.error) && (
        <div role="alert" className="rounded-[var(--radius-control)] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {(publish.error ?? restore.error) instanceof Error ? (publish.error ?? restore.error as Error).message : t.publishError}
        </div>
      )}

      {(['app', 'admin'] as const).map((surface) => (
        <AuthHeroEditor
          key={`${surface}-${presentation.data[surface].version}`}
          surface={surface}
          slot={presentation.data[surface]}
          publishing={publish.isPending && publish.variables?.surface === surface}
          restoring={restore.isPending && restore.variables === surface}
          onPublish={async (draft) => {
            await publish.mutateAsync({ surface, ...draft })
          }}
          onRestore={async () => {
            if (window.confirm(t.restoreConfirm(surface))) {
              await restore.mutateAsync(surface)
            }
          }}
        />
      ))}
    </div>
  )
}
